import { Panel } from "@/features/oversight/oversight-ui";

/**
 * Read-only policy rule recreation preview (ALL-7270).
 *
 * This is a display slice, not a policy translator, evaluator, approval or
 * activation mechanism. It renders a report that was produced elsewhere and
 * says nothing about readiness: a row is never counted as a successful mapping
 * unless the report itself reports EXACT, and a non-EXACT row always asks for a
 * named human decision even when it carries a candidate Alloy reference.
 *
 * The entire report is validated at runtime before any row renders. Malformed,
 * duplicate, truncated, unknown or unbound evidence fails closed with
 * `POLICY_RULE_MAPPING_EVIDENCE_INVALID` and no row data — a partially valid
 * report must never leak the rows that happened to validate first.
 *
 * There is deliberately no HTTP call, no fixture fallback, no browser storage
 * and no control: the route passes a typed state in, and an authorized adapter
 * is expected to supply bound real evidence later.
 */

const RULE_VERDICTS = [
  "EXACT",
  "STRICTER",
  "WEAKER",
  "UNSUPPORTED",
  "AMBIGUOUS",
  "MANUAL_REVIEW_REQUIRED",
] as const;

export type PolicyRuleMappingVerdict = (typeof RULE_VERDICTS)[number];

export type PolicyRuleMappingRule = {
  sourceRuleRef: string;
  targetRuleRef: string | null;
  state: PolicyRuleMappingVerdict;
  reason: string;
};

export type PolicyRuleMappingReadyState = {
  status: "ready";
  translationReference: string;
  sourceDigest: string;
  targetDigest: string;
  targetVersion: string;
  sourceRuleCount: number;
  evidenceSourceDigest: string;
  evidenceTargetDigest: string;
  rules: PolicyRuleMappingRule[];
};

export type PolicyRuleMappingState =
  | { status: "loading" }
  | { status: "unavailable"; reasonCode: string; supportReference?: string }
  | PolicyRuleMappingReadyState;

type ValidatedView =
  | { kind: "invalid" }
  | { kind: "loading" }
  | { kind: "unavailable"; reasonCode: string; supportReference?: string }
  | { kind: "ready"; report: PolicyRuleMappingReadyState };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function isVerdict(value: string): value is PolicyRuleMappingVerdict {
  return (RULE_VERDICTS as readonly string[]).includes(value);
}

/**
 * Validates the whole report before anything is rendered. Returns `invalid`
 * for any shape, value or cross-field binding that does not match the contract.
 */
function validateState(value: unknown): ValidatedView {
  if (!isRecord(value)) return { kind: "invalid" };

  const status = value.status;
  if (status === "loading") return { kind: "loading" };

  if (status === "unavailable") {
    if (!isNonBlankString(value.reasonCode)) return { kind: "invalid" };
    const reasonCode = value.reasonCode;
    let supportReference: string | undefined;
    if (value.supportReference !== undefined) {
      if (!isNonBlankString(value.supportReference)) return { kind: "invalid" };
      supportReference = value.supportReference;
    }
    return { kind: "unavailable", reasonCode, supportReference };
  }

  if (status !== "ready") return { kind: "invalid" };

  const translationReference = value.translationReference;
  const sourceDigest = value.sourceDigest;
  const targetDigest = value.targetDigest;
  const targetVersion = value.targetVersion;
  const evidenceSourceDigest = value.evidenceSourceDigest;
  const evidenceTargetDigest = value.evidenceTargetDigest;

  if (
    !isNonBlankString(translationReference) ||
    !isNonBlankString(sourceDigest) ||
    !isNonBlankString(targetDigest) ||
    !isNonBlankString(targetVersion) ||
    !isNonBlankString(evidenceSourceDigest) ||
    !isNonBlankString(evidenceTargetDigest)
  ) {
    return { kind: "invalid" };
  }

  // The evidence must be bound to the digests it claims to describe.
  if (evidenceSourceDigest !== sourceDigest) return { kind: "invalid" };
  if (evidenceTargetDigest !== targetDigest) return { kind: "invalid" };

  const sourceRuleCount = value.sourceRuleCount;
  if (
    typeof sourceRuleCount !== "number" ||
    !Number.isSafeInteger(sourceRuleCount) ||
    sourceRuleCount < 0
  ) {
    return { kind: "invalid" };
  }

  const rawRules = value.rules;
  if (!Array.isArray(rawRules)) return { kind: "invalid" };
  if (rawRules.length !== sourceRuleCount) return { kind: "invalid" };

  const rules: PolicyRuleMappingRule[] = [];
  const seenSourceRefs = new Set<string>();
  const seenExactTargetRefs = new Set<string>();

  for (const rawRule of rawRules) {
    if (!isRecord(rawRule)) return { kind: "invalid" };

    const sourceRuleRef = rawRule.sourceRuleRef;
    const reason = rawRule.reason;
    const state = rawRule.state;
    const targetRuleRef = rawRule.targetRuleRef;

    if (!isNonBlankString(sourceRuleRef)) return { kind: "invalid" };
    if (!isNonBlankString(reason)) return { kind: "invalid" };
    if (typeof state !== "string" || !isVerdict(state)) return { kind: "invalid" };

    if (state === "EXACT") {
      if (!isNonBlankString(targetRuleRef)) return { kind: "invalid" };
    } else if (targetRuleRef !== null && !isNonBlankString(targetRuleRef)) {
      // A non-EXACT row may carry a candidate reference or none; it may not
      // carry a blank or non-string one.
      return { kind: "invalid" };
    }

    const normalizedSourceRef = sourceRuleRef.trim();
    if (seenSourceRefs.has(normalizedSourceRef)) return { kind: "invalid" };
    seenSourceRefs.add(normalizedSourceRef);

    if (state === "EXACT") {
      const normalizedTargetRef = (targetRuleRef as string).trim();
      if (seenExactTargetRefs.has(normalizedTargetRef)) return { kind: "invalid" };
      seenExactTargetRefs.add(normalizedTargetRef);
    }

    rules.push({
      sourceRuleRef,
      targetRuleRef:
        targetRuleRef === null || targetRuleRef === undefined
          ? null
          : (targetRuleRef as string),
      state,
      reason,
    });
  }

  return {
    kind: "ready",
    report: {
      status: "ready",
      translationReference,
      sourceDigest,
      targetDigest,
      targetVersion,
      sourceRuleCount,
      evidenceSourceDigest,
      evidenceTargetDigest,
      rules,
    },
  };
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="break-all text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

function RuleRow({ rule }: { rule: PolicyRuleMappingRule }) {
  const isExact = rule.state === "EXACT";
  return (
    <li className="min-w-0 rounded-lg border border-line bg-bg px-4 py-3">
      <dl className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
            Source rule
          </dt>
          <dd className="break-all text-sm font-semibold text-ink">{rule.sourceRuleRef}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
            Alloy rule
          </dt>
          <dd className="break-all text-sm text-ink">
            {rule.targetRuleRef === null ? (
              <span className="text-muted">No Alloy rule mapping</span>
            ) : (
              rule.targetRuleRef
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
            Reported verdict
          </dt>
          <dd className="break-words text-sm font-semibold text-ink">{rule.state}</dd>
        </div>
        <div className="min-w-0 sm:col-span-2">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Reason</dt>
          <dd className="break-words text-sm text-muted">{rule.reason}</dd>
        </div>
        <div className="min-w-0 sm:col-span-2">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Decision</dt>
          <dd className="break-words text-xs font-semibold text-primary">
            {isExact ? "Reported 1:1 mapping" : "Manual decision needed"}
          </dd>
        </div>
      </dl>
    </li>
  );
}

function ReadyReport({ report }: { report: PolicyRuleMappingReadyState }) {
  return (
    <div>
      <dl className="grid gap-3 sm:grid-cols-2">
        <MetaItem label="Translation reference" value={report.translationReference} />
        <MetaItem label="Source digest" value={report.sourceDigest} />
        <MetaItem label="Target digest" value={report.targetDigest} />
        <MetaItem label="Target version" value={report.targetVersion} />
      </dl>

      {report.rules.length === 0 ? (
        <p className="mt-4 text-sm text-muted">No imported rules</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {report.rules.map((rule, index) => (
            <RuleRow key={`${index}:${rule.sourceRuleRef}`} rule={rule} />
          ))}
        </ol>
      )}
    </div>
  );
}

export function PolicyRuleMappingPreview({ state }: { state: PolicyRuleMappingState }) {
  const view = validateState(state as unknown);

  return (
    <Panel title="Policy recreation preview" bodyClassName="px-5 py-4">
      {view.kind === "loading" && (
        <p role="status" className="text-sm text-muted">
          Loading policy recreation preview...
        </p>
      )}

      {view.kind === "unavailable" && (
        <div role="status" className="rounded-lg border border-line bg-bg px-4 py-3 text-sm">
          <p className="font-semibold text-ink">Policy rule mapping is unavailable.</p>
          <p className="mt-1 break-all text-xs text-muted">{view.reasonCode}</p>
          {view.supportReference ? (
            <p className="mt-1 break-all text-xs text-muted">
              Support reference: {view.supportReference}
            </p>
          ) : null}
        </div>
      )}

      {view.kind === "invalid" && (
        <div
          role="alert"
          className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger"
        >
          <p className="font-semibold">Policy rule mapping evidence is invalid.</p>
          <p className="mt-1 break-all text-xs">POLICY_RULE_MAPPING_EVIDENCE_INVALID</p>
        </div>
      )}

      {view.kind === "ready" && <ReadyReport report={view.report} />}
    </Panel>
  );
}
